// src/App.js
import React, { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { LoadingScreen } from './components/LoadingScreen';
import { config } from './config';

// Lazy-load route entry points so the main bundle only contains shell + auth
// wiring. EnhancedMatchRoom pulls in DraftPage (~6.6k LOC) and its mobile
// twin (~8.6k LOC); keeping those out of the initial chunk meaningfully
// improves first-paint for iframe embeds.
const EnhancedMatchRoom = lazy(() => import('./components/EnhancedMatchRoom'));
const DraftPresetManager = lazy(() => import('./components/DraftPresetManager'));
const TestScoring = lazy(() => import('./TestScoring'));

const API_BASE_URL = config.API_BASE_URL;

// ตั้งค่า origin ของเว็บแม่ที่ยอมรับ postMessage ได้ (คั่นด้วย comma)
// ตัวอย่าง: REACT_APP_PARENT_ORIGINS=https://ctrlg.gg,https://staging.ctrlg.gg
const PARENT_ORIGINS = config.PARENT_ORIGINS;
const IS_PROD = process.env.NODE_ENV === 'production';

// อนุญาตรับ token จาก URL ไหม (แนะนำให้เปิดเฉพาะ dev)
const ALLOW_URL_TOKEN = config.ALLOW_URL_TOKEN;

// ถ้าเปิด จะพยายามเรียก /auth/ctrlg/exchange เพื่อเอา local token มาใช้ต่อ
const ENABLE_TOKEN_EXCHANGE = config.ENABLE_TOKEN_EXCHANGE;

function isAllowedOrigin(origin) {
  // ถ้าไม่ได้ตั้งค่า origin list:
  // - dev: อนุญาตทั้งหมดเพื่อให้ทดสอบง่าย
  // - prod: อนุญาตเฉพาะ same-origin
  if (PARENT_ORIGINS.length === 0) {
    if (process.env.NODE_ENV !== 'production') return true;
    return origin === window.location.origin;
  }
  return PARENT_ORIGINS.includes(origin);
}

async function exchangeCtrlgToken(ctrlgToken) {
  const res = await fetch(`${API_BASE_URL}/auth/ctrlg/exchange`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${ctrlgToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}), // เผื่อส่ง extra context เพิ่มภายหลัง
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Token exchange failed: ${res.status} ${text}`);
  }

  const data = await res.json();
  const localToken = data.token || data.localToken || data.accessToken;
  if (!localToken) throw new Error('Token exchange: no local token in response');
  return { localToken, user: data.user || null };
}

// Ants embed flow: parent links to https://draft-zone.ctrlg.gg?uid=&name=&exp=&sig=
// Ants HMAC-signs URL params with a shared secret; we POST them to backend to
// verify and get an internal JWT back. Parent does not embed via iframe alone -
// the user lands here directly.
async function exchangeEmbedParams(params) {
  const res = await fetch(`${API_BASE_URL}/auth/embed/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Embed exchange failed: ${res.status} ${text}`);
  }
  const data = await res.json();
  const token = data.token || data.localToken || data.accessToken;
  if (!token) throw new Error('Embed exchange: no token in response');
  return { token, user: data.user || null };
}

export default function App() {
  // สำหรับ developer: เปลี่ยนเป็น true ตอนทดสอบ scoring
  const useTestScoring = false;

  const [externalUser, setExternalUser] = React.useState(null);
  const [externalToken, setExternalToken] = React.useState(null);
  const [externalContext, setExternalContext] = React.useState(null);
  const [ctrlgToken, setCtrlgToken] = React.useState(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    // 1) CTRL G สามารถฉีด token มาก่อนโหลด bundle
    const tokenFromWindow = window.CTRLG_AUTH_TOKEN || window.CTRLG_TOKEN || null;

    // 2) สำหรับทดสอบเอง: แนบ token มากับ URL เช่น ?token=xxxxx (เฉพาะ dev)
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = ALLOW_URL_TOKEN
      ? params.get('ctrlg_token') || params.get('token') || params.get('access_token')
      : null;

    const token = tokenFromWindow || tokenFromUrl || null;
    if (token) setCtrlgToken(token);

    // 3) ants embed flow: ?uid=&name=&exp=&sig= → POST /auth/embed/exchange
    if (params.has('uid') && params.has('sig') && params.has('exp')) {
      const embedParams = Object.fromEntries(params.entries());
      exchangeEmbedParams(embedParams)
        .then(({ token: localToken, user }) => {
          setExternalToken(localToken);
          if (user) setExternalUser(user);
          // strip auth params so a back/forward doesn't replay them
          const cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
        })
        .catch((err) => {
          console.warn('[App] embed exchange failed:', err?.message);
        });
    }

    if (window.CTRLG_USER) {
      setExternalUser(window.CTRLG_USER);
    }

    if (window.CTRLG_DRAFT_CONTEXT && typeof window.CTRLG_DRAFT_CONTEXT === 'object') {
      setExternalContext(window.CTRLG_DRAFT_CONTEXT);
    }

    const onMessage = (event) => {
      try {
        if (!event || !event.data) return;
        if (!isAllowedOrigin(event.origin)) return;

        const msg = event.data;
        const payload = typeof msg === 'string' ? JSON.parse(msg) : msg;
        if (!payload || typeof payload !== 'object') return;

        if (payload.type === 'CTRLG_DRAFT_CONTEXT') {
          if (payload.token) setCtrlgToken(payload.token);
          if (payload.user) setExternalUser(payload.user);
          setExternalContext(payload);
          return;
        }

        if (payload.type === 'CTRLG_AUTH' || payload.type === 'AUTH') {
          if (payload.token) setCtrlgToken(payload.token);
          if (payload.user) setExternalUser(payload.user || null);
        }
      } catch (e) {
        // เงียบไว้เพื่อไม่ spam console ตอน embed ส่ง message ที่ไม่เกี่ยวข้อง
      }
    };

    window.addEventListener('message', onMessage);

    // ส่ง READY ไปหา parent เผื่อ parent รอฟัง และยิงซ้ำเพื่อกัน race ตอนโหลด iframe
    const sendReady = () => {
      try {
        if (window.parent && window.parent !== window) {
          const message = { type: 'CTRLG_DRAFT_READY', version: 1 };
          if (PARENT_ORIGINS.length === 0) {
            if (!IS_PROD) window.parent.postMessage(message, '*');
            return;
          }
          PARENT_ORIGINS.forEach((origin) => {
            window.parent.postMessage(message, origin);
          });
        }
      } catch (e) {}
    };

    sendReady();
    const t1 = setTimeout(sendReady, 250);
    const t2 = setTimeout(sendReady, 750);

    return () => {
      window.removeEventListener('message', onMessage);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!ctrlgToken) return;

      if (!ENABLE_TOKEN_EXCHANGE) {
        setExternalToken(ctrlgToken);
        return;
      }

      try {
        const { localToken, user } = await exchangeCtrlgToken(ctrlgToken);
        if (cancelled) return;

        setExternalToken(localToken);
        if (user) setExternalUser(user);
      } catch (err) {
        if (!cancelled) {
          console.warn('[App] token exchange failed, fallback to ctrlgToken:', err?.message);
          setExternalToken(ctrlgToken);
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [ctrlgToken]);

  if (useTestScoring) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <TestScoring />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route
          path="/"
          element={
            <EnhancedMatchRoom
              externalUser={externalUser}
              externalToken={externalToken}
              externalContext={externalContext}
            />
          }
        />
        <Route
          path="/draft-presets"
          element={<DraftPresetManager externalUser={externalUser} externalToken={externalToken} />}
        />
      </Routes>
    </Suspense>
  );
}
